<template>
    <div>
        <PageSection>
            <div class="d-flex justify-content-between align-items-baseline mb-4">
                <h4 class="mb-0">Request Credit</h4>
                <a href="#" class="small order-link d-inline-flex align-items-end gap-1" @click.prevent="goBack">
                    Go back
                    <i class="bi bi-box-arrow-up-right"></i>
                </a>
            </div>
            <div v-if="transactionData" class="row">
                <div class="text-muted mb-1">{{ transactionData.type.description }} ID</div>
                <div class="h4 mb-2">#{{ transactionData.id }}</div>
                <template v-if="transactionData.inv_order_id">
                    <div class="text-muted mb-1">Order ID</div>
                    <div class="h4 mb-2">#{{ transactionData.inv_order_id }}</div>
                </template>
                <div class="text-muted mb-0">{{ transactionData.inv_order_id ? 'Patient ' : '' }}Reference</div>
                <div class="h4 mb-0">{{ transactionData.patient_name }}</div>
            </div>
        </PageSection>
        <PageSection isLastSection="true">
            <Flash v-if="errorMessage" alert="danger">{{ errorMessage }}</Flash>
            <div class="mb-3">
                <DropdownInput
                    label="Reason"
                    v-model="request_reason"
                    :v="v$.request_reason"
                    :options="reasons"
                    disableFirstOption
                />
            </div>
            <div class="mb-3">
                <TextareaInput
                    label="Notes"
                    v-model="notes"
                    :v="v$.notes"
                    :rows="4"
                />
            </div>
            <div class="mb-3">
                <FileInput
                    label="Files"
                    v-model="files"
                    :v="v$.files"
                    :allowedExtensions="['jpg', 'jpeg', 'png', 'heic']"
                />
                <QrModal
                    v-model="files"
                    :v="v$.files"
                />
            </div>
            <div class="buttons">
                <button type="button" class="btn btn-primary me-1" @click="submit" :disabled="submitting">
                    <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Submit
                </button>
                <button type="button" class="btn btn-light" @click="goBack" :disabled="submitting">Cancel</button>
            </div>
        </PageSection>
    </div>
</template>

<style scoped lang="scss">
    .buttons
    {
        padding-top: 20px;
    }
</style>

<script>
    import api from "@/services/api";
    import PageSection from '@/components/PageSection.vue';
    import DropdownInput from '@/components/DropdownInput.vue';
    import TextareaInput from '@/components/TextareaInput.vue';
    import FileInput from '@/components/FileInput.vue';
    import QrModal from '@/components/QrModal.vue';
    import Flash from '@/components/Flash.vue';
    import useVuelidate from '@vuelidate/core';
    import { required, minLength, maxLength } from '@vuelidate/validators';

    export default {
        props: {
            account_id: { required: true },
            invoiceId: { required: true },
        },
        components: {
            PageSection,
            DropdownInput,
            TextareaInput,
            FileInput,
            QrModal,
            Flash,
        },
        setup() {
            return { v$: useVuelidate() };
        },
        data() {
            return {
                transactionData: null,
                request_reason: '',
                notes: '',
                files: [],
                reasons: [],
                submitting: false,
                errorMessage: '',
            };
        },
        validations() {
            return {
                request_reason: { required },
                notes: { required, minLength: minLength(10), maxLength: maxLength(2000) },
                files: {},
            };
        },
        mounted() {
            this.fetchTransaction();
            this.fetchReasons();
        },
        methods: {
            goBack() {
                this.$router.back();
            },
            fetchTransaction() {
                api.get(`/api/v1/accounts/${this.account_id}/transactions/${this.invoiceId}`)
                    .then((response) => {
                        this.transactionData = response.data.data;
                    })
                    .catch(() => {
                        console.log('Failed to fetch transaction');
                    });
            },
            fetchReasons() {
                api.get('/api/v1/credit-requests/reasons')
                    .then((response) => {
                        this.reasons = response.data.data;
                    })
                    .catch(() => {
                        console.log('Failed to fetch credit request reasons');
                    });
            },
            async submit() {
                this.errorMessage = '';
                this.v$.$touch();
                if (this.v$.$pending) {
                    await this.v$.$validate();
                }
                if (this.v$.$invalid) {
                    return;
                }

                this.submitting = true;
                try {
                    await api.post(`/api/v1/invoices/${this.invoiceId}/credit-request`, {
                        request_reason: this.request_reason,
                        notes: this.notes,
                        files: this.files.map(file => file.hash),
                    });
                    sessionStorage.setItem('creditRequestFlash', 'Credit successfully requested.');
                    this.goBack();
                } catch (e) {
                    this.errorMessage = e.response?.data?.message || 'There was an error submitting your credit request, please try again later.';
                } finally {
                    this.submitting = false;
                }
            },
        },
    };
</script>
